import { isObject, isString, isUndefined } from 'lodash';
import { useQuery } from 'react-query';
import { captureException } from '@sentry/react';
import { format } from 'date-fns';
import { ContentfulConfigId, getContentfulConfig } from 'domains/contentfulConfigs/contentfulConfigs';
import { ContentfulDataError } from 'domains/api/errors';
import { TypeConfigurationsFields } from 'types/contentful';
import { isBetweenHours } from 'utils/date';

export type RawLiveCallsConfigEntity = {
  workingHours: Record<string, string | null>;
  defaultTimeZone: string;
  expressablePhoneNumber: string;
};

export type LiveCallsConfig = TypeConfigurationsFields & {
  configuration: RawLiveCallsConfigEntity;
};

export const validateLivePhoneContentfulData = (rawConfig: TypeConfigurationsFields): rawConfig is LiveCallsConfig => {
  const { configuration } = rawConfig;
  if (isUndefined(configuration) || !isObject(configuration)) {
    return false;
  }
  if (!isObject(configuration.workingHours)) {
    return false;
  }
  if (!isString(configuration.defaultTimeZone)) {
    return false;
  }
  if (!isString(configuration.expressablePhoneNumber)) {
    return false;
  }

  return true;
};

export const getLiveCallsConfig = async (): Promise<LiveCallsConfig> => {
  const rawData = await getContentfulConfig(ContentfulConfigId.LIVE_PHONE_CALLS_CONFIG);

  if (!validateLivePhoneContentfulData(rawData.fields)) {
    const error = new ContentfulDataError(`Bad data for entity ID ${ContentfulConfigId.LIVE_PHONE_CALLS_CONFIG}`);
    captureException(error);
    throw error;
  }

  return rawData.fields;
};

export const isLiveCallAvailable = (data: LiveCallsConfig, currentTime: Date): boolean => {
  if (!data.toggle) {
    return false;
  }
  const currentDaySchedule = data.configuration.workingHours[format(currentTime, 'EEEE').toLowerCase()];

  if (!currentDaySchedule) return false;

  return isBetweenHours(currentTime, currentDaySchedule, data.configuration.defaultTimeZone);
};

export const useExpressablePhoneNumber = () => {
  const query = useQuery(['contentful-live-call-config'], getLiveCallsConfig, {
    select: configData => configData.configuration.expressablePhoneNumber,
  });

  return query;
};
